import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Truck, Calendar, Check } from "lucide-react";

const today = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const slug = (s) => String(s).replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();

export function ShipCompleteDialog({ open, order, carrierOptions = [], defaultCarrier = "POSTE - COL.CHRONO.", remembered = "", onConfirm, onCancel }) {
  const clientName = order?.extracted_data?.NAME || order?.extracted_data?.name || order?.extracted_data?.NOM || "";
  const [selected, setSelected] = useState(defaultCarrier);
  const [customMode, setCustomMode] = useState(false);
  const [custom, setCustom] = useState("");

  // Reset the choice every time the dialog opens
  useEffect(() => {
    if (open) { setSelected(defaultCarrier); setCustomMode(false); setCustom(""); }
  }, [open, defaultCarrier]);

  const finalCarrier = customMode ? custom.trim() : selected;

  const pillClass = (active) =>
    `inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs transition-colors ${
      active
        ? "border-orange-300 bg-orange-50 text-orange-700 font-medium"
        : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50"
    }`;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-md" data-testid="ship-complete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-orange-500" />
            Commande terminée
          </DialogTitle>
        </DialogHeader>

        <div className="text-sm text-zinc-600 space-y-3">
          <p>
            La commande{clientName ? <> de <strong className="text-zinc-800">{clientName}</strong></> : null} vient de passer en <strong className="text-green-600">TERMINE</strong>.
            Voulez-vous remplir automatiquement les colonnes d'expédition ?
          </p>

          <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3 space-y-1.5 text-xs">
            <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-zinc-400" /> <span className="text-zinc-500 w-36">SHIPPED ON</span> <span className="font-medium text-zinc-800">{today()}</span></div>
            <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-zinc-400" /> <span className="text-zinc-500 w-36">AVIS D'EXPEDITION</span> <span className="font-medium text-zinc-800">{today()}</span></div>
          </div>

          <div>
            <div className="text-xs font-medium text-zinc-600 mb-1.5 flex items-center gap-1.5">
              <Truck className="w-3.5 h-3.5 text-zinc-400" /> Transporteur (TRANS. COMPANY)
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="carrier-options">
              {carrierOptions.map((c) => {
                const active = !customMode && selected === c;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => { setCustomMode(false); setSelected(c); }}
                    className={pillClass(active)}
                    data-testid={`carrier-option-${slug(c)}`}
                  >
                    {c}{active && <Check className="w-3.5 h-3.5" />}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => setCustomMode(true)}
                className={pillClass(customMode)}
                data-testid="carrier-option-autre"
              >
                Autre…{customMode && <Check className="w-3.5 h-3.5" />}
              </button>
            </div>
            {customMode && (
              <Input
                autoFocus
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder="Nom du transporteur"
                className="mt-2 h-8 text-xs"
                data-testid="carrier-custom-input"
              />
            )}
            {remembered ? (
              <p className="text-[11px] text-zinc-400 mt-1.5" data-testid="carrier-remembered">
                Dernier choix mémorisé : <span className="text-zinc-500 font-medium">{remembered}</span>
              </p>
            ) : null}
          </div>

          <p className="text-xs text-zinc-400">Seules les cases vides seront remplies.</p>
        </div>

        <div className="flex justify-end items-center gap-2 pt-3 border-t border-zinc-100 mt-2">
          <Button variant="outline" size="sm" onClick={onCancel} data-testid="ship-complete-cancel">Non</Button>
          <Button
            size="sm"
            disabled={!finalCarrier}
            onClick={() => onConfirm(finalCarrier)}
            className="gap-1.5 bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="ship-complete-confirm"
          >
            <Truck className="w-4 h-4" /> Oui, remplir
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
